/**
 * Bozuk lazy-chunk'tan kendi kendine kurtulma.
 *
 * OLAY (2026-08-04, prod): deploy anında yeni `index.html` edge'e ulaşıp lazy
 * chunk HENÜZ ulaşmayınca Cloudflare Pages o `.js` isteğine `404` değil
 * **`200 OK` + `text/html` + `Cache-Control: public, max-age=14400`** döndürüyor.
 * Tarayıcı bunu 4 saat boyunca disk cache'inde tutuyor; dinamik import
 * "Expected a JavaScript-or-Wasm module script but the server responded with a
 * MIME type of text/html" ile patlıyor ve **sayfa hiç açılmıyor**. Kullanıcı
 * açısından tek çıkış, site verilerini elle temizlemekti.
 *
 * Asıl düzeltme `public/sw.js` (v6): HTML gelirse `cache: "reload"` ile ağdan
 * zorla tazeler. Ama o düzeltme ancak **güncellenmiş SW devraldıktan sonra**
 * devreye girer — kırık sayfayı yükleyen tur hâlâ eski SW'nin elindedir. Bu
 * modül o turu kapatır: hatayı yakalar, bir kez yeniler; ikinci turda yeni SW
 * aktiftir ve chunk düzgün gelir.
 *
 * ⚠️ Tek seferlik: `sessionStorage` bayrağı sonsuz yenileme döngüsünü keser.
 * Chunk gerçekten yoksa kullanıcı gerçek hatayı görür — sessizce dönüp durmaz.
 */

const FLAG = "chunk-recovery-reloaded";

function looksLikeChunkFailure(message: string): boolean {
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Failed to load module script") ||
    message.includes("error loading dynamically imported module") ||
    // Safari
    message.includes("Importing a module script failed")
  );
}

function recover(): void {
  try {
    if (sessionStorage.getItem(FLAG)) return; // bir kez denedik, ısrar etme
    sessionStorage.setItem(FLAG, "1");
  } catch {
    return; // sessionStorage yoksa (gizli mod kısıtı) risk alma
  }
  window.location.reload();
}

export function installChunkRecovery(): void {
  // Vite, modulepreload başarısız olduğunda bunu yayınlar.
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault?.();
    recover();
  });

  window.addEventListener("error", (e) => {
    if (typeof e.message === "string" && looksLikeChunkFailure(e.message)) recover();
  });

  window.addEventListener("unhandledrejection", (e) => {
    const msg = e.reason instanceof Error ? e.reason.message : String(e.reason ?? "");
    if (looksLikeChunkFailure(msg)) recover();
  });
}

/** Uygulama sağlıklı açıldıysa bayrağı temizle — sonraki deploy'da yine bir hak. */
export function clearChunkRecoveryFlag(): void {
  try {
    sessionStorage.removeItem(FLAG);
  } catch { /* önemsiz */ }
}
